import React from 'react';
import { Target, CheckCircle, Clock, Percent, ArrowRight } from 'lucide-react';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { CaseStudyLink } from '../components/CaseStudyLink';
import { CASE_STUDIES } from '../data/caseStudies';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';
import { StatCard } from '../components/ui/StatCard';
import { StepCard } from '../components/ui/StepCard';

export const BiznesFinderAiPage: React.FC = () => {
    const relatedCaseStudy = CASE_STUDIES.find(cs => cs.id === 'b2b-solutions');

    usePageMetadata({
        title: "BiznesFinder AI | Automatyczne Generowanie Leadów B2B",
        description: "Oszczędź 20h tygodniowo na szukaniu klientów. BiznesFinder AI dostarcza zweryfikowane leady B2B w 24h z dokładnością 97%. Pozyskuj kontakty B2B teraz!",
        schema: [
            {
                "@context": "https://schema.org",
                "@type": "Product",
                "name": "BiznesFinder AI",
                "description": "Narzędzie AI do automatycznego pozyskiwania leadów B2B. Analizuje tysiące firm, znajduje idealne kontakty i dostarcza gotowe bazy danych w 24h.",
                "brand": {
                    "@type": "Brand",
                    "name": "BiznesBezKlikania.pl"
                },
                "category": "Software > AI Automation > Lead Generation",
                "url": "https://biznesbezklikania.pl/rozwiazania/biznesfinder-ai",
                "image": "https://biznesbezklikania.pl/og-image.png",
                "offers": {
                    "@type": "Offer",
                    "availability": "https://schema.org/InStock",
                    "priceCurrency": "PLN"
                },
                "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": "4.9",
                    "reviewCount": "56",
                    "bestRating": "5"
                }
            },
            {
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "BiznesFinder AI",
                "applicationCategory": "BusinessApplication",
                "operatingSystem": "Cloud-based"
            }
        ],
        ogData: {
            type: 'product'
        }
    });

    const stats = [
        { value: '10 000+', label: 'Firm w bazie', icon: <Target className="w-6 h-6" /> },
        { value: '97%', label: 'Dokładność', icon: <Percent className="w-6 h-6" /> },
        { value: '24h', label: 'Dostawa', icon: <Clock className="w-6 h-6" /> },
        { value: '100%', label: 'Gwarancja', icon: <CheckCircle className="w-6 h-6" /> },
    ];

    const steps = [
        {
            number: 1,
            title: 'Opisz potrzeby',
            description: 'Powiedz nam, jakich firm potrzebujesz - np. "warsztaty samochodowe w Krakowie".',
        },
        {
            number: 2,
            title: 'AI Analizuje Rynek',
            description: 'Nasz system AI przeszukuje publiczne źródła i weryfikuje dane kontaktowe.',
        },
        {
            number: 3,
            title: 'Otrzymujesz Listę',
            description: 'W 24h dostajesz plik Excel z gotowymi kontaktami do Twojego działu sprzedaży.',
        },
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[70vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="mixed" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <RevealOnScroll>
                                <p className="text-sm font-semibold text-primary-light dark:text-primary-dark uppercase tracking-wider mb-4">
                                    Automatyzacja pozyskiwania leadów
                                </p>
                                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                                    BiznesFinder AI
                                </h1>
                            </RevealOnScroll>

                            <RevealOnScroll delay={100}>
                                <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark mb-8">
                                    Oszczędź 20h tygodniowo na szukaniu klientów. Dostarczamy zweryfikowane leady B2B w 24h z dokładnością 97%.
                                </p>
                            </RevealOnScroll>

                            <RevealOnScroll delay={200}>
                                <div className="space-y-4 mb-8">
                                    {['97% dokładność weryfikowanych danych', 'Dostawa gotowych kontaktów w 24h', 'Oszczędność 20 godzin tygodniowo'].map((item, i) => (
                                        <div key={i} className="flex items-center">
                                            <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mr-3">
                                                <CheckCircle className="w-4 h-4 text-green-500" />
                                            </div>
                                            <span className="text-text-primary-light dark:text-text-primary-dark">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </RevealOnScroll>

                            <RevealOnScroll delay={300}>
                                <a
                                    href="https://bartoszgaca.pl/?utm_source=biznesbezklikania&utm_medium=hero_cta_biznesfinder&utm_campaign=bbk%E2%86%92bg"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-8 py-4 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center group animate-glow"
                                >
                                    Zleć wdrożenie
                                    <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                                </a>
                            </RevealOnScroll>
                        </div>

                        <div className="grid grid-cols-2 gap-4 md:gap-6">
                            {stats.map((stat, index) => (
                                <RevealOnScroll key={stat.label} delay={index * 100}>
                                    <StatCard
                                        value={stat.value}
                                        label={stat.label}
                                        icon={stat.icon}
                                    />
                                </RevealOnScroll>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* How It Works Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Jak działa pozyskiwanie leadów z AI?
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-2xl mx-auto">
                            Prosty proces w 3 krokach - od zamówienia do gotowych kontaktów
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {steps.map((step, index) => (
                            <RevealOnScroll key={step.number} delay={index * 150}>
                                <StepCard
                                    number={step.number}
                                    title={step.title}
                                    description={step.description}
                                    animationDelay={index * 200}
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            {relatedCaseStudy && <CaseStudyLink caseStudy={relatedCaseStudy} />}
            <WhatNextSection diyLink="/rozwiazania/biznesfinder-ai" />
        </>
    );
};
